import { ChannelType, USER_QUEUE_TYPE } from '@shopee/shopee-chatbot-agent-chat/dist/types';
import { ConnectionError } from '@shopee/shopee-chatbot-agent-chat';
import { DEFAULT_ENTRY_POINT } from '@shopee/shopee-chatbot-agent-chat/dist/constants';

import { handleRequest } from '@/utils';
import LOGIN_HANDLER from '@/utils/loginHandler';
import {
  addDialogue,
  addTip,
  disableDialogueChannelOptionButton,
  updateDialogueInfo,
  updateDialogueOuterData,
} from '@/utils/sessionUtils';
import uploadMessageToSave from '@/utils/uploadMessageToSave';
import { dispatchTrack } from '@/utils/tracking';
import { handleClickHistoryButton, redirectionTo, getContent } from '@/utils/tools';
import { getTrans, bridgeCallFunc, isNum } from '@/utils/instantLoaded/tools';
import {
  TRACKING_OPERATION,
  TRACKING_SECTION,
  TRACKING_TARGET,
  INPUT_TYPE,
  GetMsgResCode,
  FAKE_SESSION_ID,
  LIVE_OFFLINE_FORM_PAGE,
  LOGIN_SUCCESS,
  ORDER_SN_TEXT,
  PROBING_FORM_EXP,
  OrderCardType,
  LIVE_AGENT_USER_TYPE,
} from '@/utils/constants';
import { BUTTON_SELECTION, TRACKING_TYPE } from '@/utils/tracking/constants';
import { userStore, initStore, agentStore, modalStore, firstInitStore, sessionStore } from '@/stores/index';
import waitEnquirySelect from '@/components/EnquiryDetailDrawerNext';
import waitOrderSelect, { OrderSelectInfo } from '@/components/OrderListDrawer';
import { EnquiryItemType, State as EnquiryItemState, LiveAgentCacheVariable } from '@/components/EnquiryList';
import { DIALOGUE_FROM, DIALOGUE_TYPE, OrderProps, TFERatingInfo } from '@/types/common';
import { getConfig, isBank, UseLiveChatSDKApp } from '@/config/core';
import { AGENT_CHANNEL, App } from '@/config/constants';
// import { queryQueueAvailability } from '@/services/chat/index';
import { QueueType } from '@/interfaces';
import { FeedbackResult } from '@/interfaces/feedback';
import { getOfflineFormUrlWithParams, getOfflineFormUrlWithParamsByApi } from '@/utils/getOfflineFormUrlWithParams';
import { waitProbingForm } from '@/components/ChatbotUI/ProbingForm';
import { ProbingFormValues } from '@shopee/chatbot-ui/lib/components/ProbingForm';
import { handleTerminalConfict } from '@/services/handleTerminalConfict';
import { ENTRY_POINT } from '@/types/entryPoints';
import getUserTypeRetentionExperiment from '@/utils/experiment/getUserTypeRetention';
import {
  goDirectlyToAgent,
  startAgent,
  checkIsAutoQueue,
  transformFrom,
  addNoLiveAgenyDialog,
  hasWebForm,
} from './agent';
import { toLiveAgentTrack } from './tracking/utils/toLiveAgentTrack';
import { generateOfflineFormUrl } from './tools';
import { formatFromInUrl } from './instantLoaded/toolsWithoutDeps';
import reporter from './report';
import { getProbingFormExperiment } from './getProbingFormExperiment';
import { TransferType, reportTransferType } from './reportTransferType';
import API, { isAnyLiveAgentAvailable } from '@/services';
import { orderListTypeToText } from '@/components/ProductList/ProductList';
import { ChannelBtnType } from '@shopee/chatbot-ui/lib/components/ChannelOptionsCard/ChannelOptionButtonsPopup';
import { handlePhoneCall, handleSubmitWebform, handleVirtualCall } from '@/components/ChatbotUI/ChannelOptionButton';
import { isDisableDisplayWithNewFlow } from './experiment/getLiveAgentDisplayLogic';
import { isAgentConnectionAutoFill } from './experiment/getAgentConnectionAutoFillLogic';
import reportSkip, { LIVE_AGENT_UI_TYPE } from './tracking/utils/liveAgentSkipTrack';
import { shouldApplyLiveAgentAvailbility } from './shouldApplyLiveAgentAvailbility';
import { isBankNonLogin } from './liveChatToken';
import { addBankToLoginDialogue, generateLoginCallbackParams, jumpToLoginPage } from './bankNonLoginHelpers';
import { shouldReportLiveAgentForNoAgent } from './shouldReportLiveAgentForNoAgent';
import { type EventData } from './chatuiEvents';
import { BankUrlMethodParamEnum, saveMethodDataById } from './bankLoginHelpers';

interface TrackingInfo {
  trackingname: string;
  trackingdata: Record<string, any>;
}

export interface ButtonInfo {
  ext_key_value?: any;
  text_key: string;
  text_value: string | number;
  uri_extra?: any;
  uri_key?: string;
  url?: string;
  extraParams?: any;
  node_id?: string;
  article_number?: string;
  extra_param?: any;
  isHistory?: boolean;
  kb_intent_id?: number;
  kb_bot_id?: number;
  kb_edition?: string;
}

export interface DialogueInfo {
  id: string;
  dialogueId: string;
  dialogueType: string;
  context?: any;
  webformReportData?: any;
}

type LiveClickInfo = {
  dialogueId: string;
  dialogueType: string;
  text: string;
  listPos?: number;
  queue_name?: string;
  user_type?: number | string;
  enquiry_type?: number | string;
  isHistory?: boolean;
  channel?: number;
  userTypeName?: string;
  triggerLiveAgentRecommender?: boolean;
  inhouseLiveAgent?: InhouseLiveAgent;
  letUserChoose?: boolean;
  queueType?: number;
  isChatbotMsgBeforeLiveAgent?: boolean;
  showProbingForm?: boolean;
  notDialogue?: boolean;
};

function sendLiveAgentRecommenderRequest({
  session_id,
  dialogue_id,
  queue_category,
  queue_category_id,
  queue_name,
  queue_from,
  inbound_order_sn,
}: {
  session_id: string;
  dialogue_id: string;
  queue_category: string;
  queue_category_id: number;
  queue_name: number | string;
  queue_from: number;
  inbound_order_sn?: string;
}) {
  handleRequest('getIntentSolution', {
    session_id,
    dialogue_id,
    queue_category,
    queue_category_id,
    queue_name,
    queue_from,
    inbound_order_sn,
    order_sn: inbound_order_sn,
    trigger_liveagent_recommender: true,
    text: 'trigger_liveagent_recommender',
    l1_reason_code: agentStore.reasonCodeL1Name,
    l1_reason_code_id: agentStore.reasonCodeL1Id,
  });
}

interface InhouseLiveAgent {
  queue_enquiry_type_id: number;
  queue_entry_point_id: number;
  queue_reason_code_id: number;
  queue_user_type_id: number;
}

type LoginClickInfo = {
  dialogueId: string;
  dialogueType: string;
  req?: any;
  isHistory?: boolean;
  data?: EventData;
};

// 埋点用 - 点击事件
export const trackingHandler = (trackingInfo: TrackingInfo) => {
  dispatchTrack({
    operation: TRACKING_OPERATION.CLICK,
    section: TRACKING_SECTION[trackingInfo.trackingname as keyof typeof TRACKING_SECTION],
    target: TRACKING_TARGET[trackingInfo.trackingname as keyof typeof TRACKING_TARGET],
    data: trackingInfo.trackingdata,
  });
};

// Live
export const handleLiveBtnClick = async (
  liveClickInfo: LiveClickInfo,
  trackingInfo?: TrackingInfo,
) => {
  let {
    dialogueId,
    dialogueType,
    text,
    listPos,
    queue_name,
    user_type,
    enquiry_type,
    isHistory,
    userTypeName,
    inhouseLiveAgent,
    letUserChoose,
    queueType,
    triggerLiveAgentRecommender,
    isChatbotMsgBeforeLiveAgent, // 是否是进线前的chatbot卡片，live agent recommander 和 case tracker
    showProbingForm = true,
    notDialogue = false,
  } = liveClickInfo;
  // 点击埋点
  trackingInfo && trackingHandler(trackingInfo);
  toLiveAgentTrack();

  // 历史消息只触发上报
  if (isHistory) {
    handleClickHistoryButton();
    return;
  }

  const showAndSaveMessage = (from: string, text: string) => {
    // 回显
    addDialogue(
      {
        dialogueId,
        dialogueType,
        from,
        type: DIALOGUE_TYPE.Msg,
        data: { content: text },
      },
      false,
    );

    // 上传消息 用于记录历史消息
    uploadMessageToSave({
      dialogueId,
      messages: [
        {
          message_text: text,
          is_sent_by_user: from === 'user',
        },
      ],
    });
  };

  const { liveAgentSetting } = initStore;
  if (!liveAgentSetting.allow_live_agent) {
    !notDialogue && showAndSaveMessage('user', text);
    showAndSaveMessage('bot', liveAgentSetting.no_live_agent_tips);
    return;
  }

  if (isBank() && isBankNonLogin) {
    const methodData = {
      liveClickInfo: {
        ...liveClickInfo
      }
    }
    const callbackParams = generateLoginCallbackParams({
      method: BankUrlMethodParamEnum.liveBtn,
      methodData: methodData,
      methodIdName: BankUrlMethodParamEnum.liveBtn
    });
    const { query } = callbackParams;
    if (query?.methodId && methodData) {
      saveMethodDataById(query.methodId, methodData);
    }
    showAndSaveMessage('user', text);
    addBankToLoginDialogue({
      dialogueId,
      dialogueType,
      callbackParams
    });
    return;
  }

  // 转人工前 先确认已登录
  LOGIN_HANDLER(async () => {
    // Live Agent Recommender 需要隐藏文本
    if (!triggerLiveAgentRecommender && !notDialogue && !isBank()) {
      showAndSaveMessage('user', text);
    }

    agentStore.updateChatChannelType();

    const hasGoneDirectlyToAgent = await goDirectlyToAgent({ listPos });

    if (hasGoneDirectlyToAgent) {
      return;
    }

    const isAutoQueue = await checkIsAutoQueue({
      letUserChoose,
      queueType,
    });

    if (isAutoQueue) {
      reportTransferType({ sessionId: initStore.sessionId, transferType: TransferType.ToLiveAgent });

      agentStore.updateAutoQueueEntryPoint(inhouseLiveAgent?.queue_entry_point_id);
      user_type = inhouseLiveAgent?.queue_user_type_id;
      enquiry_type = inhouseLiveAgent?.queue_enquiry_type_id;
    } else {
      // 非自动进线时重置autoQueueEntryPoint
      agentStore.updateAutoQueueEntryPoint();

      const shouldApplyLiveAgentAvailbilityResult = shouldApplyLiveAgentAvailbility();
      reporter.apiLog('[utils, btnClickHandler]#handleLiveBtnClick, data=', {
        level: 'debug',
        shouldApplyLiveAgentAvailbilityResult,
      });
      if (shouldApplyLiveAgentAvailbilityResult) {
        // all live agent function reply on login status
        // check whether any live agent after echo
        const isLiveAgentAvailable = await isAnyLiveAgentAvailable().catch(() => true);
        reporter.apiLog('[utils, btnClickHandler]#handleLiveBtnClick, data=', {
          level: 'debug',
          isLiveAgentAvailable,
        });
        if (!isLiveAgentAvailable) {
          // there is no any live agent available
          // addDialog based on seesionStore.bffFeatureConfig
          // refer to method `handleConnectFailure`
          // hasWebform refer to `handleConnectFailure` webFormConfig.hasWebform
          const hasWebformResult = hasWebForm();
          reporter.apiLog('[utils, btnClickHandler]#handleLiveBtnClick, hasWebformResult=', {
            level: 'debug',
            hasWebformResult,
            dialogueId,
          });
          addNoLiveAgenyDialog(dialogueId, 'live_agent_offline_text', hasWebformResult);
          shouldReportLiveAgentForNoAgent(true);
          return;
        }
      }
    }

    // 对于 SF, 有 queue name 就可以直接跳转
    // 对于 inhouse, 有 userType enquiryType 就可以直接跳转
    if (queue_name || (user_type && enquiry_type)) {
      let channelType = await agentStore.chat.getChannelType();

      // IDK 继续触发 Live Agent Recommender
      if (dialogueType === DIALOGUE_TYPE.IDK) {
        // IDK 的场景，将 lastDialogueId 更新为 currentDialogueId
        // 以便埋点获取 IDK 的 dialogueId
        sessionStore.updateDialogueId();

        sendLiveAgentRecommenderRequest({
          session_id: initStore.sessionId,
          dialogue_id: dialogueId,
          queue_category: userTypeName as string,
          queue_category_id: user_type as number,
          queue_name: enquiry_type as any,
          queue_from: channelType,
        });

        return;
      }

      if (!(user_type && enquiry_type) && channelType === ChannelType.inhouse) {
        agentStore.updateChatChannelType(ChannelType.salesforce);
        channelType = ChannelType.salesforce;
      }

      if (channelType === ChannelType.inhouse) {
        agentStore.UPDATE_AGENT_CASE_FIELD({
          ...agentStore,
          userType: parseInt(user_type as string),
          enquiryType: parseInt(enquiry_type as string),
        });

        if (showProbingForm) {
          const probingFormExperiment = getProbingFormExperiment();
          // 如果是 live agent recommander 或 case tracker 只有命中 flow1 才出现
          if (
            (isChatbotMsgBeforeLiveAgent && probingFormExperiment === PROBING_FORM_EXP.Flow1)
            || (!isChatbotMsgBeforeLiveAgent && probingFormExperiment !== PROBING_FORM_EXP.None)
          ) {
            const result = await showProbingFormFlow();

            if (Object.keys(result || {}).length === 0) {
              return;
            }
          }
        }

        startAgent(dialogueId, '', dialogueType);
      } else {
        startAgent(dialogueId, queue_name, dialogueType);
      }

      reporter.apiLog(
        `Auto queue channel:${channelType};queue_name:${queue_name};user_type:${user_type};enquiry_type:${enquiry_type}`,
        { level: 'debug' },
      );

      return;
    }

    getEnquiryInfo(
      { dialogueId, dialogueType, listPos, text, triggerLiveAgentRecommender },
      !triggerLiveAgentRecommender && !isDisableDisplayWithNewFlow(),
      getConfig()?.agentChatParams,
    );
  });
};

const getUserInfo = async (
  dialogueId: string,
  data: EnquiryItemType[],
  initEnquiry?: EnquiryItemState,
  liveAgentCacheVar?: LiveAgentCacheVariable,
): Promise<{
  userType?: EnquiryItemType;
  enquiryType?: EnquiryItemType;
  orderInfo: OrderSelectInfo;
  hasFillProbingForm?: boolean;
}> => {
  let userType = {} as EnquiryItemType | undefined;
  let enquiryType = {} as EnquiryItemType | undefined;
  let orderInfo: OrderSelectInfo = {
    inboundOrderSn: '',
    isReturned: false,
    dialogueParms: {},
    text: '',
    isClose: false,
  };
  let hasFillProbingForm = false;
  const probingFormExperiment = getProbingFormExperiment();

  if (probingFormExperiment === PROBING_FORM_EXP.Flow2) {
    const probingForm: any = await showProbingFormFlow(data);
    userType = probingForm.userType;
    enquiryType = probingForm.enquiryType;
    orderInfo.inboundOrderSn = probingForm?.order?.orderSn
      ? probingForm?.order?.orderSn === '-1'
        ? ''
        : probingForm?.order?.orderSn
      : '';

    if (Object.keys(probingForm).length > 0) {
      hasFillProbingForm = true;
    }

    return { userType, enquiryType, orderInfo, hasFillProbingForm };
  }

  const enquiryRes = await waitEnquirySelect(data, initEnquiry, liveAgentCacheVar);
  userType = enquiryRes.userType;
  enquiryType = enquiryRes.enquiryType;

  const userTypeLabel = userType?.originItem?.user_type_label ?? '';

  if (enquiryType?.isInboundOrder && userType !== undefined && enquiryType !== undefined) {
    modalStore.UPDATE_MODAL_ORDER_LIST({
      ...modalStore.modalOrderList,
      isSelectOrder: true,
    });
    if (isAgentConnectionAutoFill() && liveAgentCacheVar?.order_info?.order_sn) {
      orderInfo.inboundOrderSn = liveAgentCacheVar?.order_info?.order_sn;

      reportSkip(
        LIVE_AGENT_UI_TYPE.BUTTON_SHEET,
        TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_ORDER_INFO_SELECTION],
        undefined,
      );
    } else {
      orderInfo = await waitOrderSelect({ userTypeLabel });
    }

    // 埋点——关闭order/返回enquiry type时
    if (orderInfo.isClose) {
      dispatchTrack({
        operation: TRACKING_OPERATION.CLICK,
        target: TRACKING_TARGET.button,
        section: TRACKING_SECTION.inboundOrderSelection,
        data: {
          chat_id: '',
          user_type: `${userType?.value}`,
          enquiry_type: `${enquiryType?.value}`,
          button_selection: orderInfo.isReturned ? BUTTON_SELECTION.Back : BUTTON_SELECTION.Close,
          order_sn: '',
        },
      });
    }

    if (orderInfo.isReturned && !enquiryRes.isAutoSelect) {
      return getUserInfo(dialogueId, data, enquiryRes.enquiryItemState);
    }
  }

  const hasClose = (enquiryType?.isInboundOrder && orderInfo.isClose) || enquiryRes.isClose;

  // 中途退出弹窗只做消息上报，展示在历史消息中
  if (hasClose) {
    const messages = [];
    if (enquiryRes.userType) {
      messages.push({
        message_text: enquiryRes.userType.content,
        is_sent_by_user: true,
      });
    }
    if (enquiryRes.enquiryType) {
      messages.push({
        message_text: enquiryRes.enquiryType.content,
        is_sent_by_user: true,
      });
    }
    messages.length > 0
      && uploadMessageToSave({
        dialogueId,
        messages,
      });
  }

  // userType和enquiryType都为undefined时不会进线
  return { userType: hasClose ? undefined : userType, enquiryType: hasClose ? undefined : enquiryType, orderInfo };
};

async function showProbingFormFlow(enquiryListResponse?: Error | EnquiryItemType[] | ConnectionError) {
  // const res = enquiryListResponse || (await getEnquiryList());
  let res = enquiryListResponse;
  if (!res) {
    const enquiryRes = await getEnquiryList();
    if (enquiryRes instanceof Error) {
      return;
    }
    const { enquiryTypeInfos = [] } = enquiryRes;
    res = enquiryTypeInfos;
  }

  if (res instanceof Error || !res || res?.length === 0) {
    return;
  }

  const probingFormExperiment = getProbingFormExperiment();
  const { userType, userTypeName, enquiryType, inhouseEnquiryTypeName, inboundOrderSn } = agentStore;

  const probingForm = await waitProbingForm({
    dataSource: res,
    showUserAndEnquiryType: probingFormExperiment === PROBING_FORM_EXP.Flow2,
    showOrderSelector: probingFormExperiment === PROBING_FORM_EXP.Flow2,
    autoQueue: {
      userType: userType
        ? {
          value: userType as number,
          content: userTypeName,
        }
        : undefined,
      enquiryType: enquiryType
        ? {
          value: enquiryType as number,
          content: inhouseEnquiryTypeName,
        }
        : undefined,
      order: inboundOrderSn ? { orderSn: inboundOrderSn } : undefined,
    },
  });

  if (Object.keys(probingForm || {}).length > 0) {
    const { userType, enquiryType, problemType, order, description, files } = probingForm as ProbingFormValues;
    agentStore.UPDATE_AGENT_CASE_FIELD({
      userType: userType?.value,
      userTypeName: userType?.content,
      userTypeLabel: (userType as any)?.originItem?.user_type_label,
      enquiryType: enquiryType?.value,
      inhouseEnquiryTypeName: enquiryType?.content,
      isCanNotFindOrder: order?.orderId === -1,
      inboundOrderSn: order?.orderSn ? (order?.orderSn === '-1' ? '' : order?.orderSn) : '',
      problemType: problemType?.value as number,
      problemTypeName: problemType?.content,
      description,
      files,
      reasonCodeL1Id: enquiryType?.reasonCodeL1Id,
      reasonCodeL1Name: enquiryType?.reasonCodeL1Name,
    });
  }

  return probingForm;
}

async function getEnquiryList() {
  // 提供inquiry details
  const sessionId = initStore.sessionId === FAKE_SESSION_ID ? '' : initStore.sessionId; // sessionId为fake时传空
  const entryPointInUrl = formatFromInUrl(transformFrom());
  const res = await agentStore.chat.getEnquiryType({
    sessionId,
    entryPoint: entryPointInUrl as ENTRY_POINT,
    userQueueType: initStore?.isPremium ? USER_QUEUE_TYPE.premium : USER_QUEUE_TYPE.normal,
  });
  return res;
}

let isEnquiryInfoLoading = false;
export async function getEnquiryInfo(
  {
    dialogueId,
    dialogueType,
    listPos,
    text,
    needCheckLogin = false,
    triggerLiveAgentRecommender,
  }: LiveClickInfo & { needCheckLogin?: boolean },
  needAddDialogue = false,
  agentChatParams?: {
    channel?: AGENT_CHANNEL;
  },
) {
  if (isEnquiryInfoLoading) {
    return;
  }

  if (needCheckLogin) {
    const loginHandler = () => new Promise((resolve) => LOGIN_HANDLER(resolve, resolve));
    await loginHandler();

    if (!UseLiveChatSDKApp.is()) {
      // 登陆成功，重新请求 user_info
      await userStore.FETCH_DATA(true);
    }
  }

  isEnquiryInfoLoading = true;
  const notSessionId = !initStore.sessionId || initStore.sessionId === FAKE_SESSION_ID;
  const { channel } = agentChatParams || getConfig()?.agentChatParams || {};
  // 提供inquiry details
  // const res = await getEnquiryList();
  const resList = await Promise.all([
    getEnquiryList(),
    // 如果 能获取到 user type 的实验 就请求接口 看是否有缓存
    // 如果 session id 有问题就不查 缓存
    (getUserTypeRetentionExperiment() || isDisableDisplayWithNewFlow()) && !notSessionId
      ? handleRequest('liveAgentGetVariable', { session_id: initStore.sessionId })
      : undefined,
  ]);

  const [res, liveAgentCacheVar] = resList;

  if (res instanceof Error) {
    isEnquiryInfoLoading = false;
    console.error(res);

    switch ((res as unknown as any)?.response?.data?.code) {
      case GetMsgResCode.SESSION_ID_WRONG: // 40006
      case GetMsgResCode.SESSION_ID_MISSING: // 1004
        firstInitStore.FETCH_DATA();
        addTip(getTrans('error_session_outofdate'));
        return;
      case GetMsgResCode.MULTI_TERMINAL_CONFLICT:
        handleTerminalConfict();
        return;
    }

    agentStore.UPDATE_AGENT_CASE_FIELD();
    startAgent(dialogueId, undefined, dialogueType, { channel });
    return;
  }

  const { enquiryTypeInfos = [] } = res;

  if (!enquiryTypeInfos || enquiryTypeInfos?.length === 0) {
    isEnquiryInfoLoading = false;
    agentStore.UPDATE_AGENT_CASE_FIELD();
    startAgent(dialogueId, undefined, dialogueType, { channel });
    return;
  }

  if (!isBank() && needAddDialogue) {
    addDialogue({
      dialogueId,
      dialogueType,
      from: 'bot',
      type: DIALOGUE_TYPE.EnquiryDetails,
      data: { clickParams: [{ dialogueId, dialogueType, listPos, text }, false] },
    });

    uploadMessageToSave({
      dialogueId,
      messages: [
        {
          message_key: 'enquiry_details_desc',
          is_sent_by_user: false,
        },
      ],
    });
  }

  // 走新流程
  if (isDisableDisplayWithNewFlow() && liveAgentCacheVar) {
    let userType = {} as EnquiryItemType | undefined;
    let enquiryType = undefined as EnquiryItemType | undefined;
    let orderInfo: OrderSelectInfo | null = {
      order_id: undefined,
      inboundOrderSn: '',
      isReturned: false,
      dialogueParms: {},
      text: '',
      isClose: false,
    };

    let hasShownUserTypeSelection = false;
    let hasShownEnquiryTypeSelection = false;
    let hasShownOrderInfoSelection = false;

    const { chatbot_user_type, enquiry_type_map, order_info } = liveAgentCacheVar;

    // 是否自动填充
    if (isAgentConnectionAutoFill()) {
      userType = enquiryTypeInfos?.find(
        (item) => (item as any).originItem?.user_type_label === LIVE_AGENT_USER_TYPE[chatbot_user_type],
      );

      if (chatbot_user_type) {
        enquiryType = userType?.child?.find((item) => item.value === enquiry_type_map?.[chatbot_user_type]);
      }

      if (order_info) {
        orderInfo = order_info;
      }
    } else {
      userType = undefined;
      enquiryType = undefined;
      orderInfo = null;
    }

    agentStore.UPDATE_IN_BEFORE_AGENT(true);

    const showAndSaveMessage = (from: string, text: string) => {
      // 回显
      addDialogue(
        {
          dialogueId,
          dialogueType,
          from,
          type: DIALOGUE_TYPE.Msg,
          data: { content: text },
        },
        false,
      );

      // 上传消息 用于记录历史消息
      uploadMessageToSave({
        dialogueId,
        messages: [
          {
            message_text: text,
            is_sent_by_user: from === 'user',
          },
        ],
      });
    };

    const displayChannelButtons = async () => {
      if (
        getUserTypeRetentionExperiment()
        && (userType?.originItem?.user_type_label === LIVE_AGENT_USER_TYPE.Buyer
          || userType?.originItem?.user_type_label === LIVE_AGENT_USER_TYPE.Seller
          || orderInfo?.order_id)
        && !notSessionId
      ) {
        handleRequest('liveAgentUpdateVariable', {
          session_id: initStore.sessionId,
          chatbot_user_type: LIVE_AGENT_USER_TYPE[userType?.originItem?.user_type_label],
          order_id: orderInfo?.order_id,
        });
      }

      agentStore.UPDATE_AGENT_CASE_FIELD({
        userType: userType?.value,
        userTypeName: userType?.content,
        userTypeLabel: userType?.originItem?.user_type_label,
        enquiryType: enquiryType?.value,
        inhouseEnquiryTypeName: enquiryType?.content,
        isCanNotFindOrder: !!orderInfo?.text,
        inboundOrderSn: orderInfo?.inboundOrderSn,
        reasonCodeL1Id: enquiryType?.reasonCodeL1Id,
        reasonCodeL1Name: enquiryType?.reasonCodeL1Name,
      });

      // 放前面怕接口报错，不上报
      if (!hasShownOrderInfoSelection) {
        if (!hasShownEnquiryTypeSelection) {
          if (!hasShownUserTypeSelection) {
            reportSkip(
              LIVE_AGENT_UI_TYPE.BUBBLE,
              TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_USER_TYPE_SELECTION],
              userType?.content,
            );
          }
          reportSkip(
            LIVE_AGENT_UI_TYPE.BUBBLE,
            TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_ENQUIRY_TYPE_SELECTION],
            enquiryType?.content,
          );
        }
        reportSkip(
          LIVE_AGENT_UI_TYPE.BUBBLE,
          TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_ORDER_INFO_SELECTION],
          undefined,
        );
      }

      let res;
      try {
        res = await (API as any).getLiveAgentButtons({
          enquiry_type_id: enquiryType?.value,
          session_id: initStore.sessionId,
          app_version: window?.getAppVersion?.(),
        });
      } catch (error) {
        startAgent(dialogueId, enquiryType?.value, dialogueType, { channel });
      }
      const { buttons, should_display_buttons } = res;

      if (!should_display_buttons) {
        // 不展示buttons，则直接进线
        startAgent(dialogueId, enquiryType?.value, dialogueType, { channel });
      } else {
        addDialogue(
          {
            dialogueId: dialogueId,
            dialogueType: dialogueType,
            from: DIALOGUE_FROM.BOT,
            type: DIALOGUE_TYPE.ChannelOptionsMessage,
            data: {
              messageContent: getTrans('live_agent_fetch_channel_type_msg'),
              buttons: buttons,
              isChannelOptionsButton: true,
              onButtonsClick: (b: {
                text_value: string;
                button_type: ChannelBtnType;
                button_text: string;
                extra_param: { phone_number: any };
              }) => {
                // 拨打的是 internet call 的话 如果 app 版本不对 则让用户去更新
                if (
                  !initStore?.initSessionChatConfig?.canUseInternetCall
                  && b.button_type === ChannelBtnType.BtnVirtualCall
                ) {
                  modalStore.UPDATE_SHOW_MODAL_UPDATE_APP(true);
                  return;
                }

                // 置灰buttons
                if ([ChannelBtnType.BtnLive, ChannelBtnType.BtnVirtualCall].includes(b.button_type)) {
                  disableDialogueChannelOptionButton();
                }

                // 恢复shortcut & input
                agentStore.UPDATE_IN_BEFORE_AGENT(false);

                if (b.button_type === ChannelBtnType.BtnLive) {
                  // 展示消息及回显
                  showAndSaveMessage(DIALOGUE_FROM.USER, b.text_value || '');

                  startAgent(dialogueId, enquiryType?.value, dialogueType, { channel });
                } else if (b.button_type === ChannelBtnType.BtnSubmitWebform) {
                  handleSubmitWebform(b.text_value, { dialogueId, dialogueType, sessionId: initStore.sessionId }, {});
                } else if (b.button_type === ChannelBtnType.BtnGeneralCall) {
                  handlePhoneCall(b.text_value, {
                    dialogueId,
                    dialogueType,
                    sessionId: initStore.sessionId,
                    phoneNumber: b.extra_param.phone_number,
                  });
                } else if (b.button_type === ChannelBtnType.BtnVirtualCall) {
                  // 类型是 virtual call 就调用 virtual call
                  userStore?.UPDATE_IS_CLICK_VIRTUAL_CALL?.(true); // 标识 拨打了 virtual call
                  handleVirtualCall(
                    b.text_value,
                    {
                      dialogueId,
                      dialogueType,
                      entryPoint: ENTRY_POINT[initStore?.from as any] ?? ENTRY_POINT.general,
                      // entryPoint: '4', // 联调先写死
                      businessId: initStore?.sessionId,
                      userType: agentStore?.userType,
                      enquiryType: agentStore?.enquiryType,
                      orderSn: agentStore?.inboundOrderSn,
                      reasonCode: agentStore?.reasonCodeL1Id,
                      userTypeLabel: agentStore?.userTypeLabel,
                    },
                    {},
                  );
                }

                // 上报
                dispatchTrack({
                  operation: TRACKING_OPERATION.CLICK,
                  target: TRACKING_TARGET[TRACKING_TYPE.LIVE_AGENT_CHANNEL_SELECTION_BUTTON],
                  section: TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_CHANNEL_SELECTION_BUTTON],
                  data: {
                    chat_session_id: initStore.sessionId || '',
                    channel_type: b.button_type,
                  },
                });
              },
            },
          },
          false,
        );

        dispatchTrack({
          operation: TRACKING_OPERATION.IMPRESSION,
          target: TRACKING_TARGET[TRACKING_TYPE.LIVE_AGENT_CHANNEL_SELECTION_BUTTON],
          section: TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_CHANNEL_SELECTION_BUTTON],
          data: {
            viewed_objects: [
              {
                chat_session_id: initStore.sessionId || '',
                channel_type: buttons?.map((button: { button_type: any }) => button.button_type),
              },
            ],
          },
        });
      }
    };

    const displayOrderSelect = async () => {
      if (enquiryType?.isInboundOrder && userType !== undefined && enquiryType !== undefined) {
        if (!hasShownEnquiryTypeSelection) {
          if (!hasShownUserTypeSelection) {
            reportSkip(
              LIVE_AGENT_UI_TYPE.BUBBLE,
              TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_USER_TYPE_SELECTION],
              userType?.content,
            );
          }
          reportSkip(
            LIVE_AGENT_UI_TYPE.BUBBLE,
            TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_ENQUIRY_TYPE_SELECTION],
            enquiryType?.content,
          );
        }

        const { order_list } = await (API as any)
          .getOrderList({
            query_type: 0, // 0 means type All
            page_num: 0,
            page_size: 5,
            is_seller: userType?.originItem?.user_type_label === 'Seller' ? 1 : 0,
          })
          .catch(() => {});

        if (!order_list.length) {
          displayChannelButtons();
          return;
        }

        showAndSaveMessage(DIALOGUE_FROM.BOT, getTrans('live_agent_fetch_order_info_msg'));

        const transOrderList = order_list.map((order: any) => {
          const item = order.items[0] || {};

          return {
            ...order,
            title: item.title,
            image: item.image[0] || '',
            total_item_count: order.item_count,
            total_item_price: order.total_price,
            status: getTrans(orderListTypeToText[order.order_list_type as keyof typeof orderListTypeToText]),
            order_sn: order.order_sn,
          };
        });

        const getOrderCardType = () => {
          const new_carousel_region = ['sg', 'vn'];
          const two_row_region = ['my', 'tw', 'ph', 'br', 'id', 'co', 'cl', 'mx'];
          const vertical_region = ['th'];

          if (new_carousel_region.includes(COUNTRY)) {
            return OrderCardType.newCarousel;
          } else if (two_row_region.includes(COUNTRY)) {
            return OrderCardType.twoRowCarousel;
          } else if (vertical_region.includes(COUNTRY)) {
            return OrderCardType.verticalList;
          } else {
            return OrderCardType.twoRowCarousel;
          }
        };

        // 先把之前的order list清空
        sessionStore.UPDATE_DIALOGUE_OUTER_DATA_WITH_ORDER_LIST({ hidden: true });
        hasShownOrderInfoSelection = true;
        const curpos = addDialogue(
          {
            dialogueId: dialogueId,
            dialogueType: dialogueType,
            from: DIALOGUE_FROM.BOT,
            type: DIALOGUE_TYPE.OrderList,
            data: {
              isSelectOrder: true,
              is_seller: userType?.originItem?.user_type_label === 'Seller',
              order_list: transOrderList,
              has_more: true,
              orderCardType: getOrderCardType(),
              customSelectClick: (
                order: {
                  order_id: number | undefined;
                  order_sn: any;
                },
                index: number,
              ) => {
                updateDialogueOuterData({ hidden: true }, curpos);

                orderInfo = {
                  order_id: order?.order_id,
                  inboundOrderSn: order?.order_sn,
                  isReturned: false,
                  dialogueParms: {},
                  text: '',
                  isClose: false,
                };
                displayChannelButtons();

                dispatchTrack({
                  operation: TRACKING_OPERATION.CLICK,
                  target: TRACKING_TARGET[TRACKING_TYPE.LIVE_AGENT_ORDER_INFO_SELECTION],
                  section: TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_ORDER_INFO_SELECTION],
                  data: {
                    chat_session_id: initStore.sessionId || '',
                    order_position: index,
                  },
                });
              },
            },
          },
          false,
        );
      } else {
        displayChannelButtons();
      }
    };

    const displayEnquiryTypeSelect = (userTypeOption: { value?: any; content?: string; child: any }) => {
      hasShownEnquiryTypeSelection = true;
      if (!hasShownUserTypeSelection) {
        reportSkip(
          LIVE_AGENT_UI_TYPE.BUBBLE,
          TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_USER_TYPE_SELECTION],
          userType?.content,
        );
      }
      addDialogue(
        {
          dialogueId: dialogueId,
          dialogueType: dialogueType,
          from: DIALOGUE_FROM.BOT,
          type: DIALOGUE_TYPE.ChannelOptionsMessage,
          data: {
            messageContent: getTrans('live_agent_fetch_enquiry_type_msg'),
            disableButtons: false,
            isChannelOptionsButton: false,
            buttons: userTypeOption.child?.map(
              (enquiryTypeOption: {
                content: any;
                value:
                | {
                  value: any;
                  content: string;
                  reasonCodeL1Id?: number | undefined;
                  reasonCodeL1Name?: string | undefined;
                  child?: { value: number; content: string }[] | undefined;
                }
                | undefined;
              }) => {
                dispatchTrack({
                  operation: TRACKING_OPERATION.IMPRESSION,
                  target: TRACKING_TARGET[TRACKING_TYPE.LIVE_AGENT_ENQUIRY_TYPE_SELECTION],
                  section: TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_ENQUIRY_TYPE_SELECTION],
                  data: {
                    viewed_objects: [
                      { chat_session_id: initStore.sessionId || '', button_name: enquiryTypeOption.content },
                    ],
                  },
                });

                return {
                  text: enquiryTypeOption.content,
                  type: 'link',
                  styles: { textStyle: { textAlign: 'left', color: '#4080EE' }},
                  onClick: () => {
                    enquiryType = enquiryTypeOption;

                    showAndSaveMessage(DIALOGUE_FROM.USER, enquiryType.content || '');

                    skipStepToRightCallback();

                    dispatchTrack({
                      operation: TRACKING_OPERATION.CLICK,
                      target: TRACKING_TARGET[TRACKING_TYPE.LIVE_AGENT_ENQUIRY_TYPE_SELECTION],
                      section: TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_ENQUIRY_TYPE_SELECTION],
                      data: {
                        chat_session_id: initStore.sessionId || '',
                        button_name: enquiryTypeOption.content,
                      },
                    });
                  },
                };
              },
            ),
          },
        },
        false,
      );
    };

    const displayUserTypeSelect = () => {
      hasShownUserTypeSelection = true;
      addDialogue(
        {
          dialogueId: dialogueId,
          dialogueType: dialogueType,
          from: DIALOGUE_FROM.BOT,
          type: DIALOGUE_TYPE.ChannelOptionsMessage,
          data: {
            messageContent: getTrans('live_agent_fetch_user_type_msg'),
            disableButtons: false,
            isChannelOptionsButton: false,
            buttons: enquiryTypeInfos?.map((userTypeOption) => {
              dispatchTrack({
                operation: TRACKING_OPERATION.IMPRESSION,
                target: TRACKING_TARGET[TRACKING_TYPE.LIVE_AGENT_USER_TYPE_SELECTION],
                section: TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_USER_TYPE_SELECTION],
                data: { viewed_objects: [{ chat_session_id: initStore.sessionId || '', button_name: userTypeOption.content }] },
              });

              return {
                text: userTypeOption.content,
                type: 'link',
                styles: { textStyle: { textAlign: 'left', color: '#4080EE' }},
                // trackingData: {{
                //   "trackingsection": TRACKING_SECTION[TRACKING_TYPE.FAQ_RCMD_VIEW_MORE_BUTTON],
                //   "trackingtarget": TRACKING_TARGET[TRACKING_TYPE.FAQ_RCMD_VIEW_MORE_BUTTON],
                //   "trackingdataBasic": { chat_session_id: (initStore?.sessionId || '') }
                // }
                // },
                onClick: () => {
                  dispatchTrack({
                    operation: TRACKING_OPERATION.CLICK,
                    target: TRACKING_TARGET[TRACKING_TYPE.LIVE_AGENT_USER_TYPE_SELECTION],
                    section: TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_USER_TYPE_SELECTION],
                    data: {
                      chat_session_id: initStore.sessionId || '',
                      button_name: userTypeOption.content,
                    },
                  });

                  userType = userTypeOption;

                  showAndSaveMessage(DIALOGUE_FROM.USER, (userType as any).content || '');

                  // 0 或 1 个咨询类型，直接提交
                  if ((userType?.child || []).length <= 1) {
                    enquiryType = userType?.child?.[0];
                    displayOrderSelect();
                    return;
                  }

                  const enquiry_type_id = enquiry_type_map[LIVE_AGENT_USER_TYPE[userType?.originItem?.user_type_label]];
                  if (enquiry_type_id) {
                    enquiryType = userType?.child?.find((item: any) => item.value === enquiry_type_id);
                  }

                  skipStepToRightCallback();
                },
              };
            }),
          },
        },
        false,
      );
    };

    const displayIntroductionMsg = () => {
      showAndSaveMessage(DIALOGUE_FROM.BOT, getTrans('live_agent_instroduction_msg'));
    };

    const skipStepToRightCallback = () => {
      // 已经有了userType
      if (!userType) {
        displayUserTypeSelect();
      } else if (!enquiryType) {
        // 没有enquiryType, 走enquiryType select
        displayEnquiryTypeSelect(userType as any);
      } else if (!order_info) {
        // 有enquiryType，但没有order info，走orderSelect
        displayOrderSelect();
      } else {
        // userType、enquiryType、order info 都有，展示channel options button
        displayChannelButtons();
      }
    };
    isEnquiryInfoLoading = false;
    displayIntroductionMsg();
    skipStepToRightCallback();
    return;
  }

  const { userType, enquiryType, orderInfo, hasFillProbingForm } = await getUserInfo(
    dialogueId,
    enquiryTypeInfos,
    undefined,
    liveAgentCacheVar,
  );

  isEnquiryInfoLoading = false;

  // 关闭 userType 弹窗
  if (userType === undefined && enquiryType === undefined) {
    return;
  }

  const isVipUser = agentStore.chat.isVipUser;
  if (isVipUser && enquiryType?.vipQueue && !initStore.isPremium) {
    // normal用户是vip且配置了vip队列，则用vip队列进线
    enquiryType.value = enquiryType.vipQueue;
  }

  // 如果填写了 probing form，值在 showProbingForm中更新
  if (!hasFillProbingForm) {
    agentStore.UPDATE_AGENT_CASE_FIELD({
      userType: userType?.value,
      userTypeName: userType?.content,
      userTypeLabel: userType?.originItem?.user_type_label,
      enquiryType: enquiryType?.value,
      inhouseEnquiryTypeName: enquiryType?.content,
      isCanNotFindOrder: !!orderInfo.text,
      inboundOrderSn: orderInfo.inboundOrderSn,
      reasonCodeL1Id: enquiryType?.reasonCodeL1Id,
      reasonCodeL1Name: enquiryType?.reasonCodeL1Name,
    });
  } else {
    agentStore.UPDATE_SINGLE_AGENT_CASE_FIELD('enquiryType', enquiryType?.value);
  }

  const channelType = await agentStore.chat.getChannelType();

  if (channelType === ChannelType.inhouse) {
    agentStore.updateSelectEnquiry(`${userType?.content} ${enquiryType?.content}`);
  }

  // 用户选择的才回显文案消息
  const userSelectMessage = [];
  userType?.isUserSelect
    && userSelectMessage.push({
      dialogueId: dialogueId,
      dialogueType: dialogueType,
      from: 'user',
      type: 'Msg',
      data: { content: userType.content || '' },
    });

  enquiryType?.isUserSelect
    && userSelectMessage.push({
      dialogueId: dialogueId,
      dialogueType: dialogueType,
      from: 'user',
      type: 'Msg',
      data: { content: enquiryType.content || '' },
    });

  orderInfo?.inboundOrderSn && !isAgentConnectionAutoFill() && userSelectMessage.push(orderInfo.dialogueParms);

  orderInfo?.text
    && userSelectMessage.push({
      dialogueId: dialogueId,
      dialogueType: dialogueType,
      from: 'user',
      type: 'Msg',
      data: { content: orderInfo.text },
    });

  const curPos = addDialogue(userSelectMessage, false);

  // 上传历史消息
  const messages = [];
  if (userType?.content) {
    userType.isUserSelect
      && messages.push({
        message_text: userType.content,
        is_sent_by_user: true,
      });
  }
  if (enquiryType?.content) {
    enquiryType.isUserSelect
      && messages.push({
        message_text: enquiryType.content,
        is_sent_by_user: true,
      });
  }
  if (orderInfo.inboundOrderSn) {
    messages.push({
      message_text: `${getTrans(ORDER_SN_TEXT)}: ${orderInfo.inboundOrderSn}`,
      is_sent_by_user: true,
    });
  }
  if (orderInfo.text) {
    messages.push({
      message_text: orderInfo.text,
      is_sent_by_user: true,
    });
  }

  messages.length
    && uploadMessageToSave({
      dialogueId,
      messages,
    });

  if (isNum(curPos)) {
    // 隐藏enquiry details按钮
    listPos && updateDialogueInfo({ isDisabled: true }, listPos);
    if (triggerLiveAgentRecommender) {
      if (dialogueType === DIALOGUE_TYPE.IDK) {
        sessionStore.updateDialogueId();
      }

      sendLiveAgentRecommenderRequest({
        session_id: initStore.sessionId,
        dialogue_id: dialogueId,
        queue_category: userType?.content || '',
        queue_category_id: userType?.value,
        queue_name: enquiryType?.value,
        queue_from: channelType,
        inbound_order_sn: orderInfo.inboundOrderSn,
      });
    } else {
      const probingFormExperiment = getProbingFormExperiment();

      // 选完对列之后 命中flow1才打开probing form
      if (!hasFillProbingForm && probingFormExperiment === PROBING_FORM_EXP.Flow1) {
        const result = await showProbingFormFlow();
        if (Object.keys(result || {}).length === 0) {
          return;
        }
      }

      startAgent(dialogueId, enquiryType?.value, dialogueType, { channel });

      reporter.apiLog(
        `Manual queue channel:${channelType};queue_name:${enquiryType?.value};user_type:${userType?.value};enquiry_type:${enquiryType?.value}`,
        { level: 'debug' },
      );
    }
  }
}

// Redirection
export const handleRedirectionBtnClick = async (
  button: ButtonInfo,
  trackingInfo?: TrackingInfo,
  callback?: Function,
  extraData?: {
    closePopup?: () => void;
  },
) => {
  // 点击埋点
  trackingInfo && trackingHandler(trackingInfo);

  // 历史消息只上报
  if (button.isHistory) {
    handleClickHistoryButton();
    return;
  }

  if (button.uri_key === 'order_detail_page' && button.uri_extra) {
    button.uri_extra = {
      ...button.uri_extra,
      orderId: button.uri_extra.order_id,
    };
  }

  if (button.uri_key === 'topay_order_detail_page' && button.uri_extra) {
    button.uri_extra = { checkoutId: button.uri_extra.checkout_id };
  }

  const { webFormConfig, app } = getConfig();
  const webFormKey = webFormConfig.webFormKey || LIVE_OFFLINE_FORM_PAGE;
  // Redirection中的跳转离线表单通过generateOfflineFormUrl直接返回URL
  if (button.uri_key === webFormKey) {
    button.uri_key = '';
    button.url = generateOfflineFormUrl();
    if (app === App.Shopee) {
      button.url = await getOfflineFormUrlWithParamsByApi();
    }
    // partner app的webform链接需要带填充参数
    if (app === App.Partner) {
      button.url = await getOfflineFormUrlWithParams();
    }
  }

  redirectionTo(button.uri_key || button.url, button.uri_extra || {}, button.extraParams || {}, { closePopup: extraData?.closePopup });

  bridgeCallFunc(() => {
    const bridgeCallback = (res: { data: string }) => {
      callback && callback(res);
      window.bridgeUnRegisterHandler('viewWillReappear', bridgeCallback);
    };
    window.bridgeRegisterHandler('viewWillReappear', bridgeCallback);
  });
};

// Taskbot button
export const handleOptionBtnClick = (
  button: ButtonInfo,
  dialogueInfo: DialogueInfo,
  trackingInfo?: TrackingInfo,
  customCallback = {},
) => {
  // 点击埋点
  trackingInfo && trackingHandler(trackingInfo);

  // 历史消息只上报
  if (button.isHistory) {
    handleClickHistoryButton();
    return;
  }

  const curPos = addDialogue({
    dialogueId: dialogueInfo.dialogueId,
    dialogueType: dialogueInfo.dialogueType,
    from: 'user',
    type: DIALOGUE_TYPE.Msg,
    data: { content: getContent(button) },
  });

  if (isNum(curPos)) {
    let order_id = 0;
    if (dialogueInfo && dialogueInfo.context) {
      order_id = dialogueInfo?.context?.order_id || 0;
    }

    handleRequest('postMessage', {
      session_id: initStore.sessionId,
      dialogue_id: dialogueInfo.dialogueId,
      input_type: INPUT_TYPE.BUTTON,
      message_text: getContent(button),
      message_extra: {
        node_id: button.node_id,
        ext_key_value: button.ext_key_value,
        params: {
          order_id,
          extra_param: button.extra_param,
        },
      },
      ...customCallback,
    });
  }
};

export const handleArticleBtnClick = (button: ButtonInfo, dialogueInfo: DialogueInfo) => {
  handleRequest('getArticleByNumber', {
    article_number: button.article_number,
    dialogue_id: dialogueInfo.dialogueId,
    message_text: getContent(button),
    session_id: initStore.sessionId || '',
  });
};

export const handleFeedbackBtnClick = (
  option: ButtonInfo,
  dialogueInfo: DialogueInfo,
  trackingInfo?: TrackingInfo,
  callback?: (res: any) => void,
) => {
  trackingInfo && trackingHandler(trackingInfo);

  // 历史消息只上报
  if (option.isHistory) {
    handleClickHistoryButton();
    return;
  }

  const { sessionId } = initStore;
  const { dialogueId, dialogueType } = dialogueInfo;

  const curPos = addDialogue({
    dialogueId,
    dialogueType,
    from: 'user',
    type: DIALOGUE_TYPE.Msg,
    data: { content: getContent(option) },
  });
  if (isNum(curPos)) {
    handleRequest('postFeedback', {
      session_id: sessionId,
      dialogue_id: dialogueId,
      feedback: option.text_value,
      message_text: getContent(option),
    }).then(callback);
  }
};

export const handleNewFeedbackBtnClick = (
  option: ButtonInfo,
  dialogueInfo: DialogueInfo,
  trackingInfo?: TrackingInfo,
  tfeRatingInfo?: TFERatingInfo,
  callback?: (res: any) => void,
) => {
  // 历史消息只上报
  trackingInfo && trackingHandler(trackingInfo);

  const { sessionId } = initStore;
  const { id, dialogueId, webformReportData } = dialogueInfo;
  const params: {
    session_id: string;
    dialogue_id: string;
    message_id: string;
    feedback: FeedbackResult;
    tfe_rating_info?: string;
  } = {
    session_id: sessionId,
    dialogue_id: dialogueId,
    message_id: id,
    feedback: option.text_value as FeedbackResult,
  };

  if (tfeRatingInfo) {
    tfeRatingInfo.rating = option.text_value as FeedbackResult;
    params.tfe_rating_info = JSON.stringify(tfeRatingInfo);
  }

  handleRequest('postFeedback', params, { noLoading: true }).then(callback);
  if (webformReportData) {
    trackingHandler({
      trackingname: TRACKING_TYPE.WEBFORM_FEEDBACK_BUTTON,
      trackingdata: {
        ...webformReportData,
        webform_feedback: option.text_value,
        create_time: new Date().getTime(),
        tenant_id: 1,
      },
    });
  }
};

export const handlePopupArticleBtnClick = (
  button: ButtonInfo,
  dialogueInfo: DialogueInfo,
  trackingInfo?: TrackingInfo,
  callback?: (res: any) => void,
) => {
  trackingInfo && trackingHandler(trackingInfo);

  const { sessionId } = initStore;

  handleRequest('getArticle', {
    article_num: button.article_number,
    kb_intent_id: button.kb_intent_id,
    kb_edition: button.kb_edition,
    current_session_id: sessionId,
  }).then(callback);
};

export const handleOrderRedirect = (order: Partial<OrderProps>, trackingInfo?: TrackingInfo) => {
  trackingInfo && trackingHandler(trackingInfo);

  switch (order.uri_key) {
    case 'order_detail_page_seller': {
      redirectionTo(order.uri_key, {
        orderID: order.order_id,
        shopID: order.shop_id,
        source: 'chatbot',
      });
      break;
    }
    case 'order_detail_page': {
      redirectionTo(order.uri_key, { orderId: order.order_id });
      break;
    }
    case 'topay_order_detail_page': {
      redirectionTo(order.uri_key, { checkoutId: order.checkout_id });
      break;
    }
    case 'topay_order_detail_page_seller': {
      redirectionTo(order.uri_key, {
        orderID: order.order_id,
        shopID: order.shop_id,
        source: 'chatbot',
      });
      break;
    }
    case 'shopeefood_order_detail_page_buyer': {
      redirectionTo(order.uri_key, { orderId: order.order_id });
      break;
    }
    default: {
      redirectionTo(order.uri_key, order);
      break;
    }
  }
};

export const handleClickMoreOrder = (
  { nodePointId, dialogueId, dialogueType, taskbotName, isHistory }: any,
  button: any,
  trackingInfo?: TrackingInfo,
) => {
  trackingInfo && trackingHandler(trackingInfo);

  // 历史消息只上报
  if (isHistory) {
    handleClickHistoryButton();
    return;
  }
  modalStore.UPDATE_MODAL_ORDER_LIST({
    nodePointId,
    dialogueId,
    dialogueType,
    taskbotName,
    node_id: button.node_id || '',
    is_seller: button.is_seller || false,
  });
  modalStore.SHOW_ORDERLIST_MODAL();
};

// Login
export const handleLoginBtnClick = async (
  { dialogueId, dialogueType, req, isHistory, data }: LoginClickInfo,
  trackingInfo?: TrackingInfo,
) => {
  // 点击埋点
  trackingInfo && trackingHandler(trackingInfo);
  toLiveAgentTrack();

  // 历史消息只触发上报
  if (isHistory) {
    handleClickHistoryButton();
    return;
  }

  if (isBank()) {
    let actionData = null;
    if (data?.action?.task_bot_action) {
      const action = data.action;
      actionData = {
        ...(action.customCallback || {}),
        message_text: data?.text,
        input_type: INPUT_TYPE.BUTTON,
        session_id: initStore.sessionId,
        dialogue_id: dialogueId,
        thread_node_id: action?.info?.threadNodeId,
        thread_id: action?.info?.threadId,
        thread_id_v2: action?.info?.threadIdV2,
        round_id: action?.info?.roundId,
        is_bypass: action?.task_bot_action?.is_bypass || false,
        message_extra: {task_bot_action: action?.task_bot_action},
      };
    }
    jumpToLoginPage({actionData: actionData,});
    return;
  }

  LOGIN_HANDLER(() => {
    // 返回提示：Yay...
    addDialogue({
      dialogueId: dialogueId,
      dialogueType: dialogueType,
      from: 'bot',
      type: 'Msg',
      data: { content: getTrans(LOGIN_SUCCESS) },
    });

    uploadMessageToSave({
      dialogueId,
      messages: [
        {
          message_text: getTrans(LOGIN_SUCCESS),
          is_sent_by_user: false,
        },
      ],
    });

    // 登陆成功，重新请求 user_info
    userStore.FETCH_DATA(true);

    // 重发请求，回填user向输出dialogue的dialogue信息
    req
      && handleRequest(req.request, req.params).then((res: { dialogue_id: any; dialogue_type: any }) => {
        res
          && req.curPos
          && updateDialogueInfo(
            {
              dialogueId: res.dialogue_id,
              dialogueType: res.dialogue_type,
            },
            req.curPos,
          );
      });
  });
};
